// Shared date helpers for the Projects table filtering + filter dropdown.
// Bid End Date can arrive in a few shapes (ISO "YYYY-MM-DD", "09/Jan/2026",
// "16/01/2026"); Date Submitted arrives as ISO. parseProjectDate normalises
// any of them to a local-midnight Date so range comparisons are timezone-safe.

const MONTH_MAP = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

export const parseProjectDate = (value) => {
  if (!value || value === "_" || String(value).trim() === "") return null;
  const str = String(value).trim();

  // "09/Jan/2026" (DD/MMM/YYYY)
  let m = str.match(/^(\d{2})\/([a-zA-Z]{3})\/(\d{4})$/);
  if (m) {
    const month = MONTH_MAP[m[2].toLowerCase()];
    if (month !== undefined) return new Date(Number(m[3]), month, Number(m[1]));
  }

  // "16/01/2026" (DD/MM/YYYY)
  m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));

  // "2026-05-19" (YYYY-MM-DD) — build at local midnight, never UTC, to avoid
  // the off-by-one day shift `new Date("2026-05-19")` produces in -ve offsets.
  m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

  const fallback = new Date(str);
  if (isNaN(fallback.getTime())) return null;
  return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
};

// Format a Date as "YYYY-MM-DD" using local parts (matches what the table sends).
export const toISODate = (date) => {
  if (!date) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const DATE_PRESETS = [
  { key: "all", label: "All" },
  { key: "last_week", label: "Last Week" },
  { key: "last_month", label: "Last Month" },
  { key: "last_year", label: "Last Year" },
  { key: "custom", label: "Custom" },
];

export const emptyDateFilter = () => ({ preset: "all", start: null, end: null });

// Resolve a date-filter section to a concrete [from, to] local-midnight window.
// Returns null when no constraint should apply (preset "all" or incomplete custom).
export const resolveDateRange = (section, now = new Date()) => {
  if (!section || !section.preset || section.preset === "all") return null;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (section.preset === "custom") {
    const start = parseProjectDate(section.start);
    const end = parseProjectDate(section.end) || start;
    if (!start) return null;
    const from = start <= end ? start : end;
    const to = start <= end ? end : start;
    return { from, to };
  }

  const from = new Date(today);
  if (section.preset === "last_week") from.setDate(from.getDate() - 7);
  else if (section.preset === "last_month") from.setMonth(from.getMonth() - 1);
  else if (section.preset === "last_year") from.setFullYear(from.getFullYear() - 1);
  else return null;

  return { from, to: today };
};

// True if a project's date value falls inside the section's resolved window.
// When a constraint is active, projects without a date are excluded.
export const matchesDateRange = (value, section, now = new Date()) => {
  const range = resolveDateRange(section, now);
  if (!range) return true;
  const date = parseProjectDate(value);
  if (!date) return false;
  return date >= range.from && date <= range.to;
};

// Short human label for the current selection, shown on the filter row.
export const dateFilterLabel = (section) => {
  if (!section || !section.preset || section.preset === "all") return "Any Time";
  if (section.preset === "custom") {
    const start = parseProjectDate(section.start);
    if (!start) return "Custom";
    const end = parseProjectDate(section.end);
    const fmt = (d) =>
      d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    if (end && end.toDateString() !== start.toDateString()) {
      return `${fmt(start)} – ${fmt(end)}`;
    }
    return fmt(start);
  }
  return (DATE_PRESETS.find((p) => p.key === section.preset) || {}).label || "Any Time";
};
